/**
 * Database Explorer — schema introspection contracts (read-only).
 *
 * Imported by:
 *   - excellent/app/api/database/schema/.../route.ts        (server)
 *   - packages/ui/src/blocks/database/                       (client)
 *
 * The zod shapes mirror the interfaces in packages/core/src/db-introspect.ts.
 * Keep them in lockstep — if a field is added to a Db* interface, add it here.
 */

import { z } from "zod";
import type { Operation } from "../../types";

export const dbColumnSchema = z.object({
  cid: z.number(),
  name: z.string(),
  type: z.string(),
  notnull: z.boolean(),
  dflt_value: z.string().nullable(),
  pk: z.number(),
});
export type DbColumnDto = z.infer<typeof dbColumnSchema>;

export const dbForeignKeySchema = z.object({
  id: z.number(),
  seq: z.number(),
  table: z.string(),
  from: z.string(),
  to: z.string(),
  on_update: z.string(),
  on_delete: z.string(),
  match: z.string(),
});
export type DbForeignKeyDto = z.infer<typeof dbForeignKeySchema>;

export const dbIndexSchema = z.object({
  name: z.string(),
  unique: z.boolean(),
  columns: z.array(z.string()),
  origin: z.string(),
});

export const dbTriggerSchema = z.object({
  name: z.string(),
  sql: z.string(),
});

const objectType = z.enum(["table", "view", "virtual"]);

export const dbObjectSchema = z.object({
  name: z.string(),
  type: objectType,
  group: z.string(),
  system: z.boolean(),
  rowCount: z.number(),
  ddl: z.string(),
});
export type DbObjectDto = z.infer<typeof dbObjectSchema>;

export const dbTableDetailSchema = dbObjectSchema.extend({
  columns: z.array(dbColumnSchema),
  foreignKeys: z.array(dbForeignKeySchema),
  indexes: z.array(dbIndexSchema),
  triggers: z.array(dbTriggerSchema),
});
export type DbTableDetailDto = z.infer<typeof dbTableDetailSchema>;

export const dbRelationshipSchema = z.object({
  from: z.string(),
  to: z.string(),
  columns: z.array(z.object({ from: z.string(), to: z.string() })),
});
export type DbRelationshipDto = z.infer<typeof dbRelationshipSchema>;

export const dbConnectionInfoSchema = z.object({
  path: z.string(),
  sizeBytes: z.number(),
  pageSize: z.number(),
  pageCount: z.number(),
  journalMode: z.string(),
  foreignKeys: z.boolean(),
  sqliteVersion: z.string(),
  objectCount: z.number(),
  migrations: z.array(z.object({ product: z.string(), version: z.number() })),
});
export type DbConnectionInfoDto = z.infer<typeof dbConnectionInfoSchema>;

export const schemaConnection: Operation<
  void,
  void,
  void,
  z.infer<typeof dbConnectionInfoSchema>
> = {
  method: "GET",
  path: () => "/api/database/schema/connection",
  response: dbConnectionInfoSchema,
};

export const schemaObjects: Operation<
  void,
  void,
  void,
  z.infer<typeof dbObjectSchema>[]
> = {
  method: "GET",
  path: () => "/api/database/schema/objects",
  response: z.array(dbObjectSchema),
};

export const schemaObjectQuery = z.object({ name: z.string().min(1) });

export const schemaObject: Operation<
  void,
  z.infer<typeof schemaObjectQuery>,
  void,
  z.infer<typeof dbTableDetailSchema>
> = {
  method: "GET",
  path: () => "/api/database/schema/object",
  query: schemaObjectQuery,
  response: dbTableDetailSchema,
};

export const schemaRelationships: Operation<
  void,
  void,
  void,
  z.infer<typeof dbRelationshipSchema>[]
> = {
  method: "GET",
  path: () => "/api/database/schema/relationships",
  response: z.array(dbRelationshipSchema),
};

// "Back up now" — copies data.db to data.db.bak-<ISO> (PRD §5.1, §13.7).
export const backupResultSchema = z.object({
  path: z.string(),
  sizeBytes: z.number(),
});
export type BackupResultDto = z.infer<typeof backupResultSchema>;

export const schemaBackup: Operation<
  void,
  void,
  void,
  z.infer<typeof backupResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/backup",
  response: backupResultSchema,
};

// ── RO-3-11 (#44304) — two reads the ported pages need ──────────────────────

/** One applied migration (`_migrations`), as the Migrations page tabulates it. */
export const appliedMigrationSchema = z.object({
  product: z.string(),
  version: z.number(),
  applied_at: z.string(),
});
export type AppliedMigrationDto = z.infer<typeof appliedMigrationSchema>;

export const schemaMigrationsResponse = z.object({ rows: z.array(appliedMigrationSchema) });
export type SchemaMigrationsResponse = z.infer<typeof schemaMigrationsResponse>;

/**
 * READ — the full `_migrations` ledger, ordered by product then version.
 *
 * `schemaConnection` already reports a `migrations` array, and this is
 * deliberately NOT that: connection's is a per-product HIGH-WATER MARK
 * (`{product, version}`) for the header facts, while `/database/migrations`
 * tabulates every applied row WITH its `applied_at` so it can show "last
 * applied". Widening the connection payload instead would have made a header
 * read carry a table's worth of rows on every page that shows the `.db` path.
 *
 * Read-only introspection of the migration ledger — no DDL, no `runSql`, so it
 * is `READ`, not the `db:mutate` class the `ddl.*` routes carry.
 */
export const schemaMigrations: Operation<
  void,
  void,
  void,
  z.infer<typeof schemaMigrationsResponse>
> = {
  method: "GET",
  path: () => "/api/database/schema/migrations",
  response: schemaMigrationsResponse,
};

export const schemaMeaningQuery = z.object({ name: z.string().min(1) });

export const schemaMeaningResponse = z.object({
  /** Table-level meaning from the semantic registry, or null if undocumented. */
  meaning: z.string().nullable(),
  /** column name → meaning, keyed to the table's REAL columns only. */
  columnMeanings: z.record(z.string(), z.string()),
});
export type SchemaMeaningResponse = z.infer<typeof schemaMeaningResponse>;

/**
 * READ — the semantic registry's prose for one table (Data Design §5.4).
 *
 * A SECOND operation rather than extra fields on `schemaObject`: `schemaObject`
 * is the structural introspection the table editor, the switcher and the grid all
 * call, and only the schema DETAIL page renders meanings. Bolting optional fields
 * onto the shared read would either make every caller pay for them or make the
 * Next route and the Hono mount disagree about whether they are populated — and
 * a contract whose response depends on WHO mounted it is worse than two contracts.
 */
export const schemaMeaning: Operation<
  void,
  z.infer<typeof schemaMeaningQuery>,
  void,
  z.infer<typeof schemaMeaningResponse>
> = {
  method: "GET",
  path: () => "/api/database/schema/meaning",
  query: schemaMeaningQuery,
  response: schemaMeaningResponse,
};

export const schema = {
  connection: schemaConnection,
  objects: schemaObjects,
  object: schemaObject,
  relationships: schemaRelationships,
  backup: schemaBackup,
  migrations: schemaMigrations,
  meaning: schemaMeaning,
};
